import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Newspaper, SlidersHorizontal, Star, FileText, LogOut, Radio,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/feed", label: "Feed", icon: Newspaper, testid: "nav-x-feed-link" },
  { to: "/inputs", label: "Mashup", icon: SlidersHorizontal, testid: "nav-mashup-inputs-link" },
  { to: "/favorites", label: "Favorites", icon: Star, testid: "nav-favorites-link" },
  { to: "/export", label: "Studio", icon: FileText, testid: "nav-export-studio-link" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[#0A0D12] relative z-[2]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0D12]/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/feed" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 grid place-items-center">
              <Radio className="w-5 h-5 text-[#06231a]" strokeWidth={2.5} />
            </div>
            <span className="font-serif text-xl font-extrabold tracking-tight text-zinc-50 hidden sm:block">
              Pulse<span className="text-emerald-400">Forge</span>
            </span>
          </Link>

          <nav className="flex items-center gap-1 bg-[#121721] rounded-full p-1 border border-white/10">
            {NAV.map(({ to, label, icon: Icon, testid }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    active
                      ? "bg-emerald-500 text-[#06231a]"
                      : "text-zinc-400 hover:text-zinc-100 hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </Link>
              );
            })}
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-menu-button"
                className="w-9 h-9 rounded-full bg-[#1A212E] border border-white/10 grid place-items-center overflow-hidden hover:border-emerald-400/50 transition-colors"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-mono font-semibold text-emerald-400">{initials}</span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121721] border-white/10 text-zinc-200">
              <div className="px-2 py-1.5 text-xs text-zinc-400 font-mono truncate max-w-[200px]">{user?.email}</div>
              <DropdownMenuItem
                data-testid="logout-button"
                className="text-zinc-200 focus:bg-white/5 focus:text-white cursor-pointer"
                onClick={async () => { await logout(); navigate("/login"); }}
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">{children}</main>
    </div>
  );
}
